import { Fragment } from 'react';
import { ChevronRightIcon, HomeIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

interface Segment {
  name: string;
  path: string;
}

function getSegments(path: string): Segment[] {
  const parts = path.split('/').filter(Boolean);
  return parts.map((name, index) => ({
    name,
    path: parts.slice(0, index + 1).join('/'),
  }));
}

/**
 * Clickable ancestors of the current folder. Clicking any ancestor (including
 * the root) navigates there and clears the selected file/history offset, the
 * same reset a folder-row click performs — the two are the same "change
 * folder" action from different rows (data-model.md).
 */
export function Breadcrumb({
  path,
  onNavigate,
}: {
  path: string;
  onNavigate: (path: string) => void;
}) {
  const segments = getSegments(path);

  return (
    <nav
      // Not "Breadcrumb": the shell's own breadcrumb is already called that and
      // renders above this one on every files route. Two navigation landmarks
      // with one name tell a reader there are two of something and nothing
      // about which is which.
      aria-label="File path"
      className="text-muted-foreground flex min-w-0 items-center gap-1 text-sm"
    >
      <button
        type="button"
        onClick={() => onNavigate('')}
        className={cn(
          'hover:text-foreground flex shrink-0 items-center gap-1 rounded px-1 py-0.5 pointer-coarse:min-h-11',
          path === '' && 'text-foreground font-medium',
        )}
      >
        <HomeIcon aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        Files
      </button>
      {segments.map((segment, index) => (
        <Fragment key={segment.path}>
          <ChevronRightIcon
            aria-hidden="true"
            className="size-3.5 shrink-0"
            strokeWidth={1.75}
          />
          <button
            type="button"
            onClick={() => onNavigate(segment.path)}
            className={cn(
              'hover:text-foreground truncate rounded px-1 py-0.5 pointer-coarse:min-h-11',
              index === segments.length - 1 && 'text-foreground font-medium',
            )}
          >
            {segment.name}
          </button>
        </Fragment>
      ))}
    </nav>
  );
}
